import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
    MultiPageVideoModel,
    ElementTypes,
    VideoElement,
    AudioElement,
    SpecificationExporter,
    type TranscriptCue,
} from '@bynder-studio/render-core';
import { List, Thumbnail, Flex, token, notify } from '@bynder/design-system';
import { IconArrowLeft, IconTextStyle, IconArrowRight } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { parseSrt } from './parseSrt';
import TranscriptGenerateModal from './TranscriptGenerateModal';
import TranscriptStyles from './TranscriptStyles';
import TranscriptForm from './TranscriptForm';
import { getCreative } from 'packages/store/creatives/creatives.selectors';
import { getElementsFonts, getFonts } from 'packages/store/user/user.selectors';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { getDefaultFontId } from 'packages/pages/editor/utils/getDefaultFontId';
import { StyledDivider } from './Component.styled';
import transcribeService from 'packages/services/transcribeService';
import { prepareDataForTranscribe } from 'packages/common/editor/editor.bloc';

const TRANSCRIBE_TIMEOUT_MS = 300_000; // 5 minutes

const NO_AUDIO_ERROR = 'NO_AUDIO_ELEMENTS';

const isNoAudioMessage = (message: unknown): boolean =>
    typeof message === 'string' && message.toLowerCase().includes('no audio');

const hasEnabledAudioSource = (creativeModel: MultiPageVideoModel): boolean => {
    const currentModel = creativeModel.getCurrentModel?.();

    if (!currentModel) {
        return false;
    }

    const globalAudioHasSource = Boolean(
        creativeModel.getGlobalAudioTrack1?.()?.src || creativeModel.getGlobalAudioTrack2?.()?.src,
    );

    if (globalAudioHasSource) {
        return true;
    }

    const elements = currentModel.getAllElementsRecursively?.() || currentModel.getElements?.() || [];

    return elements.some((element: unknown) => {
        if (element instanceof AudioElement) {
            return true;
        }

        if (element instanceof VideoElement) {
            return element.useAudio === true;
        }

        return false;
    });
};

const waitForTranscribeCompleted = (jobId: number): Promise<void> =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error('TRANSCRIBE_TIMEOUT'));
        }, TRANSCRIBE_TIMEOUT_MS);

        const cleanup = transcribeService.listenToTranscribeStatus(
            jobId,
            (status) => {
                if (status === 'COMPLETED') {
                    clearTimeout(timer);
                    resolve();
                } else if (status === 'RENDER_FAILED' || status === 'TRANSCRIPTION_FAILED') {
                    clearTimeout(timer);
                    reject(new Error(status));
                }
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });

const fetchSrtFromTranscribeJob = async (jobId: number): Promise<string> => {
    const response = await transcribeService.getTranscribeUrl(jobId);

    if (response.status >= 400 || !response.json?.signedUrl) {
        throw new Error('TRANSCRIBE_URL_MISSING');
    }

    const srtResponse = await fetch(response.json.signedUrl);

    if (!srtResponse.ok) {
        throw new Error('FAILED_TO_FETCH_TRANSCRIBE_SRT');
    }

    const srt = await srtResponse.text();

    if (!srt.trim()) {
        throw new Error('TRANSCRIBE_RESULT_EMPTY');
    }

    return srt;
};

type TranscriptContext = 'editor' | 'content-configurator';

export const TranscriptListItem = ({
    creativeModel,
    onClick,
    context = 'editor',
    isInitialTemplateTranscriptEnabled = false,
}: {
    creativeModel: MultiPageVideoModel;
    onClick: () => void;
    context?: TranscriptContext;
    isInitialTemplateTranscriptEnabled?: boolean;
}) => {
    const { translate } = useTranslate();

    return (
        <List.Item
            thumbnail={<Thumbnail variant="clean" icon={<IconTextStyle />} />}
            onClick={onClick}
            subtext={
                creativeModel?.getTranscriptElement?.().enabled
                    ? translate('editor.transcript.subtext.enabled')
                    : translate('editor.transcript.subtext.disabled')
            }
            rightElements={<IconArrowRight />}
        >
            {translate('editor.transcript.title')}
        </List.Item>
    );
};

export const TranscriptBackButton = ({ onBack }: { onBack: () => void }) => {
    const { translate } = useTranslate();

    return (
        <List>
            <List.Item
                onClick={onBack}
                thumbnail={
                    <Flex alignItems="center">
                        <StyledBackIcon />
                        <Thumbnail variant="clean" icon={<IconTextStyle />} />
                    </Flex>
                }
            >
                {translate('editor.transcript.title')}
            </List.Item>
        </List>
    );
};

const Transcript = ({
    creativeModel,
    onBack,
    showEnabledToggle = true,
    showStyles = true,
    showBackButton = true,
    context = 'editor',
    variationPageId,
    variationId,
    pageIndex,
    isInitialTemplateTranscriptEnabled = false,
    onSaveVariation = null,
}: {
    creativeModel: MultiPageVideoModel;
    onBack: () => void;
    showEnabledToggle?: boolean;
    showStyles?: boolean;
    showBackButton?: boolean;
    context?: TranscriptContext;
    variationPageId?: string;
    variationId?: string;
    pageIndex?: number;
    isInitialTemplateTranscriptEnabled?: boolean;
    onSaveVariation?: (
        variationId: string,
        callback?: () => void,
        resetPages?: number[],
    ) => Promise<string | undefined>;
}) => {
    const { translate } = useTranslate();
    const [cues, setCues] = useState<TranscriptCue[]>([]);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const fonts = useSelector(getFonts);
    const elementsFonts = useSelector(getElementsFonts);
    const design = useSelector(getCreative);
    const forceUpdate = useForceUpdate();
    const transcriptElement = creativeModel.getTranscriptElement();

    const effectiveTranscriptEnabled = transcriptElement.enabled || isInitialTemplateTranscriptEnabled;
    const enabled = showEnabledToggle ? effectiveTranscriptEnabled : true;
    const shouldEnableTranscriptFromTemplate = isInitialTemplateTranscriptEnabled && !transcriptElement.enabled;

    useEffect(() => {
        if (shouldEnableTranscriptFromTemplate) {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                enabled: true,
            });
        }
    }, [creativeModel, shouldEnableTranscriptFromTemplate]);

    // Hydrate cues from the model whenever the active variation/page context changes.
    useEffect(() => {
        setCues(creativeModel.getTranscriptElement().cues ?? []);
    }, [creativeModel, variationPageId, pageIndex, transcriptElement.cues]);

    const hasTranscribableMedia = useMemo(() => {
        const currentModel = creativeModel.getCurrentModel?.();

        if (!currentModel) {
            return false;
        }

        const elements = currentModel.getAllElementsRecursively?.() || currentModel.getElements?.() || [];

        return elements.some((element: any) => {
            return element instanceof VideoElement || element instanceof AudioElement;
        });
    }, [creativeModel]);

    const defaultFontId = useMemo(() => getDefaultFontId(elementsFonts, fonts), [elementsFonts, fonts]);

    const handleToggleEnabled = useCallback(() => {
        const defaultProperties = transcriptElement.fontId === null ? { fontId: defaultFontId } : {};

        creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
            enabled: !enabled,
            ...defaultProperties,
        });
        forceUpdate();
    }, [creativeModel, enabled, transcriptElement?.fontId, defaultFontId, forceUpdate]);

    const handleOpenGenerateModal = useCallback(() => {
        setShowGenerateModal(true);
    }, []);

    const handleCancelGenerate = useCallback(() => {
        setShowGenerateModal(false);
    }, []);

    const currentPageMeta = creativeModel.getModelsMetaData()[creativeModel.getCurrentPageIndex()];
    const pageName = currentPageMeta?.name || '';
    const designName = design?.name || '';

    const handleCuesChange = useCallback(
        (newCues: TranscriptCue[]) => {
            setCues(newCues);

            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                enabled: true,
                cues: newCues,
                fontId: transcriptElement.fontId ?? defaultFontId,
            });
        },
        [creativeModel, defaultFontId, transcriptElement.fontId],
    );

    const handleGenerate = useCallback(
        async (languageCode: string | null) => {
            setIsGenerating(true);

            try {
                const rawElements = SpecificationExporter.exportElements(creativeModel.getCurrentModel().getElements());
                const { elements } = prepareDataForTranscribe(rawElements);
                const duration = creativeModel.getPlaybackDuration().duration;
                let createJobResponse = null;

                if (context === 'editor') {
                    const jobData: { elements: unknown[]; duration: number; languageCode?: string } = {
                        elements,
                        duration,
                    };

                    if (languageCode) {
                        jobData.languageCode = languageCode;
                    }

                    createJobResponse = await transcribeService.createTranscribeJob(jobData);
                } else if (variationPageId && variationId) {
                    // Saving regenerates the variation and produces a NEW per-page
                    // variation id. Re-read the fresh id returned by the save instead
                    // of the stale `variationPageId` prop captured in this closure.
                    const savedVariationPageId = await onSaveVariation(variationId);

                    if (!savedVariationPageId) {
                        throw new Error('VARIATION_ID_IS_MISSING');
                    }

                    const jobData: { variationId: string; languageCode?: string } = {
                        variationId: savedVariationPageId,
                    };

                    if (languageCode) {
                        jobData.languageCode = languageCode;
                    }

                    createJobResponse = await transcribeService.createVariationTranscribeJob(jobData);
                }

                if (!createJobResponse) {
                    throw new Error('VARIATION_ID_IS_MISSING');
                }

                if (createJobResponse.status >= 400 || !createJobResponse.json?.id) {
                    if (isNoAudioMessage(createJobResponse.json?.message)) {
                        throw new Error(NO_AUDIO_ERROR);
                    }

                    throw new Error('FAILED_TO_CREATE_TRANSCRIBE_JOB');
                }

                const jobId = createJobResponse.json.id;

                await waitForTranscribeCompleted(jobId);

                const resolvedSrt = await fetchSrtFromTranscribeJob(jobId);
                const parsedCues = parseSrt(resolvedSrt);

                if (!parsedCues.length) {
                    throw new Error('TRANSCRIBE_RESULT_EMPTY');
                }

                setCues(parsedCues);
                creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                    enabled: true,
                    cues: parsedCues,
                    fontId: defaultFontId,
                });

                setShowGenerateModal(false);
            } catch (error) {
                const isNoAudio = error instanceof Error && error.message === NO_AUDIO_ERROR;
                const isAudioMuted = isNoAudio && !hasEnabledAudioSource(creativeModel);

                let descriptionKey = 'editor.transcript.generate.error.description';

                if (isAudioMuted) {
                    descriptionKey = 'editor.transcript.generate.error.audio-off';
                } else if (isNoAudio) {
                    descriptionKey = 'editor.transcript.generate.error.no-audio';
                }

                notify({
                    title: translate('editor.transcript.generate.error.title'),
                    description: translate(descriptionKey),
                    variant: 'error',
                });
            } finally {
                setIsGenerating(false);
            }
        },
        [context, creativeModel, defaultFontId, variationPageId, onSaveVariation, variationId, translate],
    );

    return (
        <>
            {showBackButton && <TranscriptBackButton onBack={onBack} />}
            {showEnabledToggle && (
                <List>
                    <List.Item checkedVariant="switch" isChecked={enabled} onClick={handleToggleEnabled}>
                        {translate('editor.transcript.enable')}
                    </List.Item>
                </List>
            )}
            {enabled && (
                <TranscriptForm
                    cues={cues}
                    isGenerating={isGenerating}
                    designName={designName}
                    pageName={pageName}
                    onGenerate={handleOpenGenerateModal}
                    onCuesChange={handleCuesChange}
                    isDisabled={!hasTranscribableMedia}
                />
            )}
            {enabled && showStyles && (
                <>
                    <StyledDivider />
                    <TranscriptStyles creativeModel={creativeModel} />
                </>
            )}
            {showGenerateModal && (
                <TranscriptGenerateModal
                    isGenerating={isGenerating}
                    onGenerate={handleGenerate}
                    onCancel={handleCancelGenerate}
                />
            )}
        </>
    );
};

const StyledBackIcon = styled(IconArrowLeft)`
    margin-right: ${token.spacing3};
`;

export default Transcript;
